import React, { useCallback, useEffect, useRef } from 'react';
// import {t} from '@lingui/macro';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Badge, Layout, Menu, Tag } from 'antd';
import {
    BankOutlined,
    BarChartOutlined,
    BookOutlined,
    ClusterOutlined,
    CodeSandboxOutlined,
    ContactsOutlined,
    ControlOutlined,
    DollarOutlined,
    FileTextOutlined,
    InboxOutlined,
    MinusSquareOutlined,
    PlusSquareOutlined,
    QuestionOutlined,
    ShoppingCartOutlined,
    ShopOutlined,
    FileProtectOutlined,
    RobotOutlined,
    AppstoreAddOutlined,
    CoffeeOutlined,
} from '@ant-design/icons';
import { getSidebarOptions } from './options';
import Scrollbars from 'bukku/components/ScrollBar';
import SidebarStyled from './sidebar.style';
import AppActions from 'bukku/redux/App/actions';
import Logo from 'bukku/components/Logo';
import { siteConfig } from 'bukku/settings';
import { LockIcon } from 'bukku/components/Access/common/LockIcon';
import { LockTooltip } from 'bukku/components/Access/common/LockTooltip';
import {StorageKey} from "../../enums/StorageKey";
import {AccessSpecs} from "../../components/Access/AccessChecker/AccessSpecs";
import {AccessChecker} from "../../components/Access/AccessChecker";
import {withI18n} from "@lingui/react";
import { useListManager } from '../../redux/List/helper';
import { L } from '../../redux/List/reducer';
import { AppName } from '../AppPage/ControlPanel/Apps/enums/AppName';
import { SupportButton } from '../../components/SupportButton';
import { PlanType } from '../../enums/PlanType';
import { SubscriptionStatus } from '../../enums/SubscriptionStatus';

const { Sider } = Layout;

const {
    changeCurrent,
    toggleDrawerOpen,
    changeOpenKeys,
    toggleCollapsed,
} = AppActions;

export const Icons = {
    'bar-chart': BarChartOutlined,
    robot: RobotOutlined,
    'plus-square': PlusSquareOutlined,
    'minus-square': MinusSquareOutlined,
    inbox: InboxOutlined,
    bank: BankOutlined,
    contacts: ContactsOutlined,
    'code-sandbox': CodeSandboxOutlined,
    'file-text': FileTextOutlined,
    book: BookOutlined,
    control: ControlOutlined,
    store: ShoppingCartOutlined,
    cluster: ClusterOutlined,
    dollar: DollarOutlined,
    flow: ShopOutlined,
    myinvois: FileProtectOutlined,
    minipos: AppstoreAddOutlined,
    admin: CoffeeOutlined,
};

const openOnHoverMode = true;

// React Component
function Sidebar(props) {
    const listManager = useListManager([L.flow_streams]);
    const {
        drawerCollapsedMode,
        drawerOpen,
        openKeys,
        current,
        // updateAvailable,
        timeoutAvailable,
        user,
        shoeboxBadgeCount,
        view,
        favouriteReports,
        streamsList,
        settings,
        apps,
    } = props;
    const {
        // changeCurrent,
        changeOpenKeys,
        toggleCollapsed,
        toggleDrawerOpen,
    } = props;

    const collapsed = drawerCollapsedMode && !drawerOpen;
    const dispatch = useDispatch();

    // Timer refs for hover open/close
    const hoverTimerRef = useRef(null);
    const HOVER_DELAY = 200; // 200ms

    useEffect(() => {
        // dispatch(readAllStreams());
        listManager.updateLists(); // force read list
        // (when connecting new stream - already handled in Flow Saga for HTTP stream,
        // oAuth2 will always refresh page - so not needed)
    }, [dispatch, listManager]);

    const onClick = useCallback(() => {
        if (view === 'MobileView') {
            setTimeout(() => {
                toggleCollapsed();
                toggleDrawerOpen();
            }, 100);
        }
    }, [toggleCollapsed, toggleDrawerOpen, view]);

    const onSiderClick = useCallback(() => {
        // console.log('aaa Sidebar onSiderClick', {openOnHoverMode, drawerOpen, toggleCollapsed});

        switch (view) {
            case 'DesktopView':
            case 'TabView':
                if (hoverTimerRef.current) {
                    clearTimeout(hoverTimerRef.current); // reset hover timer
                    hoverTimerRef.current = null;
                }

                toggleDrawerOpen(true);
                break;
            case 'MobileView':
            default:
                break;
        }
    }, [toggleDrawerOpen, view]);

    const onOpenChange = useCallback(newOpenKeys => {
        const latestOpenKey = newOpenKeys.find(
            key => !(openKeys.indexOf(key) > -1),
        );
        const latestCloseKey = openKeys.find(
            key => !(newOpenKeys.indexOf(key) > -1),
        );
        let nextOpenKeys = [];
        if (latestOpenKey)
            nextOpenKeys = nextOpenKeys.concat(latestOpenKey);

        if (latestCloseKey)
            nextOpenKeys = [];

        changeOpenKeys(nextOpenKeys);
    }, [changeOpenKeys, openKeys]);

    const onMouseEnter = useCallback(() => {
        // console.log('aaa onMouseEnter', {openOnHoverMode, drawerOpen, toggleCollapsed});
        if (openOnHoverMode && !drawerOpen) {
            switch (view) {
                case 'DesktopView':
                case 'TabView':
                    // add some delay to avoid accidental hover open
                    hoverTimerRef.current = setTimeout(() => {
                        toggleDrawerOpen(true);
                    }, HOVER_DELAY);
                    break;
                case 'MobileView':
                default:
                    toggleDrawerOpen();
                    break;
            }
        }
    }, [drawerOpen, toggleDrawerOpen, view]);

    const onMouseLeave = useCallback(() => {
        if (hoverTimerRef.current)
            clearTimeout(hoverTimerRef.current); // reset hover timer

        if (openOnHoverMode && drawerOpen)
            toggleDrawerOpen(false);
    }, [drawerOpen, toggleDrawerOpen]);

    function getOptions() {
        if (!user)
            return [];

        const options = getSidebarOptions(streamsList).map(option => {
            const item = {...option};
            if (item.children) {
                item.children = item.children
                    .map(childOption => {
                        const child = {...childOption};

                        const url = child.key;
                        const accessSpec = new AccessSpecs().getFromUrl(url);
                        const {
                            permission,
                            subscription,
                            integration,
                            miscellaneous,
                        } = new AccessChecker(accessSpec).detailedCheck();

                        if (child.key === '/myinvois/minipos' && !(apps.find(app => app.name === AppName.MINIPOS && app.is_activated)))
                            child.hidden = true;

                        child.locked = !subscription;
                        child.hidden = !permission || !integration || child.hidden || !miscellaneous;

                        return child;
                    })
                    .filter(child => !child.hidden);

                if (option.key === '/reports') {
                    item.children = item.children.filter(childOption => {
                        if (childOption.key === '/reports/all')
                            return true;
                        return !!favouriteReports.find(r => r === childOption.key);
                    });
                }

                if (option.key === '/myinvois' && !settings.is_myinvois_submission_enabled)
                    item.hidden = true;

                if (option.key === '/minipos' && !(apps.find(app => app.name === AppName.MINIPOS && app.is_activated)))
                    item.hidden = true;

                item.locked = !item.children.find(child => !child.locked);
                item.hidden = !item.children.length || item.hidden;
            }
            else {
                const url = item.key;
                const accessSpec = new AccessSpecs().getFromUrl(url);
                const {
                    permission,
                    subscription,
                    integration,
                    miscellaneous,
                } = new AccessChecker(accessSpec).detailedCheck();

                item.locked = !subscription;
                item.hidden = !permission || !integration || !miscellaneous;

                // financing-portal has no children
                if (item.key === '/financing-portal' && !settings.financing_portal_on)
                    item.hidden = true;

                // if (item.key === '/dashboard')
                //     item.hidden = false
            }
            return item;
        });
        return options.filter(item => !item.hidden);
    }

    function getShoeboxBadge(key) {
        if (key !== '/shoebox' || current[0] === '/shoebox')
            return false;
        return (
            <span className="shoebox-count-badge">
                <Badge count={shoeboxBadgeCount} />
            </span>
        );
    }

    function renderLock(isParent) {
        return <LockIcon
            className={`sidebar-locked-icon${isParent ? " sidebar-locked-icon-parent" : ''}`}
        />;
    }

    function renderMainItem(option) {
        const { key, label, leftIcon, locked, isNew, isPreview } = option;

        const Icon = Icons[leftIcon] || QuestionOutlined;

        let textDiv = (
            <span className="sidebar-text">
                { label }
                {isNew && !locked ? (
                    <span style={{ paddingLeft: 4 }}>
                        <Tag color="#f50">new</Tag>
                    </span>
                ) : null}
                {isPreview && !locked ? (
                    <span style={{ paddingLeft: 4 }}>
                        <Tag color="#08979c">preview</Tag>
                    </span>
                ) : null}
                { getShoeboxBadge(key) }
                { locked ? renderLock(true) : null }
            </span>
        );
        if (locked) {
            textDiv = (
                <span style={{marginInlineStart: 0}}>
                    <LockTooltip placement="right">
                        {textDiv}
                    </LockTooltip>
                </span>
            );
        }

        return (
            <span
                style={{display: "flex"}}
                className={`sidebar-main-item ${locked ? "sidebar-main-item-locked" : ''}`}
            >
                <Icon className="sidebar-icon" />
                { textDiv }
            </span>
        );
    }

    function renderSubItem(option) {
        const { label, isNew, locked } = option;

        let textDiv = (
            <span className={`sidebar-submenu-text ${locked ? 'sidebar-submenu-text-locked' : ''}`}>
                { label }
                { isNew && !locked ? (
                    <span style={{ paddingLeft: 4 }}>
                        <Tag color="#f50">new</Tag>
                    </span>
                ) : null}
                {locked ? renderLock(false) : null }
            </span>
        );

        if (locked) {
            textDiv = (
                <LockTooltip placement="right">
                    {textDiv}
                </LockTooltip>
            );
        }

        return textDiv;
    }

    const getChildrenOption = child => {
        let itemDiv = renderSubItem(child);
        if (!child.locked) {
            itemDiv = (
                <Link
                    to={child.key}
                    tabIndex="-1"
                >
                    {itemDiv}
                </Link>
            );
        }
        return (
            {
                key: child.key,
                className: child.locked ? "ant-menu-item-locked" : "",
                label: itemDiv,
            }
        );
    };

    function getMenuOption(option) {
        const { key, children, locked } = option;

        if (children && !locked) {
            return (
                {
                    key,
                    title: renderMainItem(option),
                    label: renderMainItem(option),
                    popupClassName: "sidebar",
                    children: children.map(getChildrenOption),
                }
            );
        }

        let itemDiv = renderMainItem(option);
        if (!locked) {
            itemDiv = (
                <Link
                    to={key}
                    tabIndex="-1"
                >
                    {itemDiv}
                </Link>
            );
        }

        // No child
        return (
            {
                key,
                className: locked ? "ant-menu-item-locked" : "",
                label: itemDiv,
            }
        );
    }

    function renderSupportButton() {
        const {
            subscription,
            planInUse,
        } = props;

        if (
            (
                planInUse?.type === PlanType.ZERO ||
                planInUse?.type === PlanType.LAUNCH ||
                planInUse?.type === PlanType.LAUNCH_2025
            ) && (
                subscription?.status !== SubscriptionStatus.trial &&
                subscription?.status !== SubscriptionStatus.active_trial
            )
        )
            return (<SupportButton href="mailto:customer@bukku.my" />);
        return null;
    }

    const options = getOptions();
    const menuItems = options.map(getMenuOption);
    return (
        <SidebarStyled id="bukku-sidebar">
            <Sider
                trigger={null}
                collapsible
                collapsed={collapsed}
                width={240}
                collapsedWidth={70}
                className="isomorphicSidebar"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={onSiderClick}
                style={{transition: "none"}}
            >
                <Logo
                    collapsed={collapsed}
                    siteConfig={siteConfig}
                />
                <Scrollbars
                    className={
                        `scroll-area` +
                        // `${updateAvailable ? " update-available" : ""}` +
                        `${timeoutAvailable ? " timeout-available" : ""}`
                    }
                >
                    <Menu
                        onClick={onClick}
                        theme="dark"
                        className="isoDashboardMenu"
                        getPopupContainer={() => document.getElementById('bukku-sidebar')}
                        mode={collapsed ? 'vertical' : 'inline'}
                        openKeys={collapsed ? [] : openKeys}
                        selectedKeys={current}
                        onOpenChange={onOpenChange}
                        items={menuItems}
                    />
                    {/* { options.map(renderOption) }
                    </Menu> */}
                </Scrollbars>
                { renderSupportButton() }
            </Sider>
        </SidebarStyled>
    );
}

export default connect(state => ({
    view: state.App.view,
    drawerCollapsedMode: state.App.drawerCollapsedMode,
    drawerOpen: state.App.drawerOpen,
    openKeys: state.App.openKeys,
    current: state.App.current,
    user: state.Auth.user,
    company: state.Auth.company,
    apps: state.Auth.apps,
    features: state.Auth.features,
    shoeboxBadgeCount: state.App.shoeboxBadgeCount,
    // updateAvailable: state.App.updateAvailable,
    timeoutAvailable: state.App.timeoutAvailable,
    favouriteReports: state.LocalStorage[StorageKey.cache_favourite_reports],
    streams: state.Flow.streams,
    streamsList: state.List.flowStreams,
    settings: state.List.settings,
    subscription: state.Auth.subscription,
    planInUse: state.Subscription.planInUse,
}), {
    toggleDrawerOpen,
    changeCurrent,
    changeOpenKeys,
    toggleCollapsed,
})(withI18n()(Sidebar));
